import { abbreviate } from '@chainflip/utils/string';
import { Link } from '@/shared/components/atoms/Link';
import { ValidatorBadges } from '@/shared/components/atoms/ValidatorBadges';
import Button from '@/shared/components/flip-ui-kit/Button';
import CopyButton from '@/shared/components/molecules/CopyButton';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { usePendingRedemption } from '@/shared/hooks/funding';
import { type OperatorValidator } from '@/shared/hooks/useOperator';
import ArrowIcon from '@/shared/icons/flip-ui-kit/small/Arrow';
import MinusCircleIcon from '@/shared/icons/flip-ui-kit/small/MinusCircle';
import PlusCircleIcon from '@/shared/icons/flip-ui-kit/small/PlusCircle';
import { buildChainflipExplorerLink } from '@/shared/utils/chainflip';
import { formatWithNumeral } from '@/shared/utils/numbers';

const ValidatorRow = ({
  validator,
  redeemFunds,
  fundValidator,
}: {
  validator: OperatorValidator;
  redeemFunds?: (accountId: `cF${string}`) => void;
  fundValidator?: (accountId: `cF${string}`) => void;
}) => {
  const { data: redemption = null } = usePendingRedemption(
    redeemFunds ? validator.idSs58 : undefined,
  );

  return (
    <tr className="text-12">
      <td>
        <div className="flex flex-col">
          {validator.alias}
          <div className="flex items-center gap-x-1">
            <Link
              href={buildChainflipExplorerLink(validator.idSs58, 'validators')!}
              target={process.env.NEXT_PUBLIC_EXPLORER_URL ? '_blank' : undefined}
              rel="noreferrer"
              underline
              noPropagate
              color="light"
              className="whitespace-nowrap"
            >
              {abbreviate(validator.idSs58, 4)}
            </Link>
            <CopyButton textToCopy={validator.idSs58} />
          </div>
        </div>
      </td>
      <td>
        <div className="grid grid-cols-[repeat(2,minmax(67px,1fr))] justify-items-center gap-2">
          <ValidatorBadges
            halfOpacity={!validator.cacheValidator?.isQualified}
            cacheValidator={validator.cacheValidator}
            badges={['Authority', 'Backup', 'Qualified']}
          />
        </div>
      </td>
      <td>
        <FlipLabel amount={validator.balance} className="font-aeonikMedium text-12" />
      </td>
      <td>
        <div className="flex items-center">
          <FlipLabel
            amount={validator.totalRewards}
            className="font-aeonikMedium text-12"
            prefix={validator.totalRewards.gt(0) ? '+' : ''}
          />
        </div>
      </td>
      <td>
        <FlipLabel amount={validator.redeemable} className="font-aeonikMedium text-12" />
      </td>
      <td>{formatWithNumeral(validator.reputation)} / 2,880</td>
      <td>{validator.cfeVersion}</td>
      {redeemFunds && fundValidator && (
        <td>
          <div className="flex gap-x-2">
            <Button
              className="whitespace-nowrap"
              icon={
                redemption ? (
                  <ArrowIcon className="ml-1 text-cf-orange-1" direction="right" />
                ) : (
                  <MinusCircleIcon className="mr-1 group-enabled:text-cf-red-2" />
                )
              }
              type="secondary-standard"
              iconPos={redemption ? 'right' : 'left'}
              size="small"
              onClick={() => redeemFunds(validator.idSs58)}
              disabled={!redemption && validator.redeemable.eq(0)}
            >
              {redemption ? 'Continue' : 'Redeem'}
            </Button>
            <Button
              icon={<PlusCircleIcon className="mr-1" />}
              type="primary-standard"
              size="small"
              iconPos="left"
              onClick={() => fundValidator(validator.idSs58)}
              className="whitespace-nowrap"
            >
              Add funds
            </Button>
          </div>
        </td>
      )}
    </tr>
  );
};

export type ValidatorsTableProps = {
  validators: OperatorValidator[];
  isLoading: boolean;
  redeemFunds?: (accountId: `cF${string}`) => void;
  fundValidator?: (accountId: `cF${string}`) => void;
};

export const ValidatorsTable = ({
  validators,
  fundValidator,
  redeemFunds,
  isLoading,
}: ValidatorsTableProps) => (
  <TableV2
    columns={[
      'Validator ID',
      { name: 'Badges', labelHidden: true },
      'Balance',
      'Total Rewards',
      'Redeemable',
      'Reputation',
      'CFE Version',
      ...(redeemFunds && fundValidator
        ? [
            {
              name: 'Actions',
              labelHidden: true,
              alignment: 'right',
            } as const,
          ]
        : []),
    ]}
    className="bg-cf-gray-3"
    isLoading={isLoading}
    rows={
      validators.map((validator) => (
        <ValidatorRow
          key={validator.idSs58}
          validator={validator}
          redeemFunds={redeemFunds}
          fundValidator={fundValidator}
        />
      )) ?? []
    }
  />
);
